import { Node, mergeAttributes } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import VariableBlockView from "./VariableBlockView.vue";

export type VariableAlign = "left" | "center" | "right" | "none";

export interface VariableBlockOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    variableBlock: {
      insertVariableBlock: (attrs: {
        variable: string;
        label: string;
        width?: string;
        height?: string;
        align?: VariableAlign;
      }) => ReturnType;
    };
  }
}

export function getVariableAlignStyle(align: VariableAlign): Record<string, string> {
  if (align === "left") return { display: "inline-block", float: "left", margin: "0 8px 0 0" };
  if (align === "center") return { display: "block", margin: "0 auto" };
  if (align === "right") return { display: "inline-block", float: "right", margin: "0 0 0 8px" };
  return { display: "inline-block", verticalAlign: "middle", margin: "0 4px" };
}

function styleObjectToString(style: Record<string, string>): string {
  return Object.entries(style)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${v}`)
    .join("; ");
}

export const VariableBlock = Node.create<VariableBlockOptions>({
  name: "variableBlock",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      variable: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-variable-image"),
        renderHTML: (attributes: Record<string, any>) => ({
          "data-variable-image": attributes.variable,
        }),
      },
      label: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-label"),
        renderHTML: (attributes: Record<string, any>) => ({
          "data-label": attributes.label,
        }),
      },
      width: {
        default: "200px",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-width") || "200px",
        renderHTML: (attributes: Record<string, any>) => ({
          "data-width": attributes.width,
        }),
      },
      height: {
        default: "120px",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-height") || "120px",
        renderHTML: (attributes: Record<string, any>) => ({
          "data-height": attributes.height,
        }),
      },
      align: {
        default: "none",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-align") || "none",
        renderHTML: (attributes: Record<string, any>) => ({
          "data-align": attributes.align,
        }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: "span[data-variable-image]", priority: 1001 },
      { tag: "div[data-variable-image]", priority: 1001 },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const width = HTMLAttributes["data-width"] || "200px";
    const height = HTMLAttributes["data-height"] || "120px";
    const align = (HTMLAttributes["data-align"] || "none") as VariableAlign;
    const alignStyle = styleObjectToString(getVariableAlignStyle(align));

    return [
      "span",
      {
        class: "variable-image-block-wrapper",
        style: `width: ${width}; height: ${height}; ${alignStyle}`,
      },
      [
        "span",
        mergeAttributes(this.options.HTMLAttributes, {
          "data-variable-image": HTMLAttributes["data-variable-image"],
          "data-label": HTMLAttributes["data-label"],
          "data-width": width,
          "data-height": height,
          "data-align": align,
          class: "variable-image-block",
          style: "display: block; width: 100%; height: 100%;",
        }),
        `{{${HTMLAttributes["data-variable-image"]}}}`,
      ],
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(VariableBlockView);
  },

  addCommands() {
    return {
      insertVariableBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              variable: attrs.variable,
              label: attrs.label,
              width: attrs.width || "200px",
              height: attrs.height || "120px",
              align: attrs.align || "none",
            },
          });
        },
    };
  },
});
